/** Which transport carries the live session (the socket, or long polling).
 *
 * The socket is the default and stays the default. `LIVE_TRANSPORT=poll` at build time makes
 * long polling the only transport, for a deployment behind a proxy that will never pass a
 * WebSocket (apps/web/vite.config.ts, docs/RUNBOOK-DEPLOY.md). And a
 * socket that never opens -- refused twice before a single frame -- hands over to long polling
 * on its own, so a student on a network nobody configured for us still gets the lesson. A
 * socket that opened once and then dropped is a network blip, not a policy, and keeps
 * reconnecting as before. */
import { LivePoll, type LivePollOptions } from "./pollTransport";
import { LiveSocket, type ConnState } from "./socket";

declare const __LIVE_TRANSPORT__: string;

export type LiveTransportName = "socket" | "poll";

/** What this build was told to use; anything but "poll" is the socket. */
export function configuredTransport(value: string = typeof __LIVE_TRANSPORT__ === "string" ? __LIVE_TRANSPORT__ : "socket"): LiveTransportName {
  return value === "poll" ? "poll" : "socket";
}

/** Consecutive socket failures, with no frame ever received, before long polling takes over. */
export const SOCKET_FAILURES_BEFORE_POLL = 2;

export type LiveConnectionOptions = LivePollOptions & {
  transport?: LiveTransportName;
  /** Told which transport is carrying the session, whenever that changes. */
  onTransport?: (t: LiveTransportName) => void;
  /** Test seams: how each transport is built. */
  makeSocket?: (o: LivePollOptions) => Transport;
  makePoll?: (o: LivePollOptions) => Transport;
};

type Transport = { connect(): void; send(payload: Record<string, unknown>): void; close(): void };

export class LiveConnection {
  private current: Transport;
  private name: LiveTransportName;
  private failures = 0;
  private opened = false;
  private closed = false;
  private readonly opts: LiveConnectionOptions;

  // plain fields rather than parameter properties: node --test strips types, it does not compile
  constructor(opts: LiveConnectionOptions) {
    this.opts = opts;
    this.name = opts.transport ?? configuredTransport();
    this.current = this.build(this.name);
  }

  connect(): void {
    this.opts.onTransport?.(this.name);
    this.current.connect();
  }

  send(payload: Record<string, unknown>): void {
    this.current.send(payload);
  }

  close(): void {
    this.closed = true;
    this.current.close();
  }

  get transport(): LiveTransportName {
    return this.name;
  }

  private build(name: LiveTransportName): Transport {
    const onState = (s: ConnState) => {
      if (name === "socket" && !this.opened && s === "reconnecting") {
        this.failures += 1;
        if (this.failures >= SOCKET_FAILURES_BEFORE_POLL && !this.closed) {
          this.switchToPoll();
          return;
        }
      }
      if (s === "live") this.opened = true;
      if (this.current === transport) this.opts.onState(s);
    };
    const o: LivePollOptions = { ...this.opts, onState };
    const transport =
      name === "poll"
        ? (this.opts.makePoll ?? ((x) => new LivePoll(x)))(o)
        : (this.opts.makeSocket ?? ((x) => new LiveSocket(x)))(o);
    return transport;
  }

  private switchToPoll(): void {
    const old = this.current;
    this.name = "poll";
    this.current = this.build("poll");
    old.close();
    this.opts.onTransport?.("poll");
    this.current.connect();
  }
}
